/**
 * Finanzas API module.
 *
 * Endpoints relacionados al módulo "Finanzas del aseador":
 *   - `getMisFinanzasAseador`      → aseador (toma id JWT, sin :id).
 *   - `getFinanzasAseadorById`     → admin/superadmin viendo a otro aseador.
 *
 * Los endpoints legacy `/api/finanzas/:id`, `/porFecha/:id` y
 * `/cliente/:id` siguen vivos en el backend (los pueden usar otros
 * consumidores) pero ya no tienen wrapper en el frontend — la página
 * `/finanzas` legacy fue removida en Round 2026-04-29 (ver
 * `docs/modulo-finanzas-aseador.md`).
 */

import { apiFetch } from "./apiClient";

const ERROR_MSG = "Error al obtener finanzas personales";

/**
 * Shape devuelto por GET /api/finanzas/aseador/me.
 *
 * Resumen mensual del sueldo del aseador logueado: por cada mes en el rango
 * solicitado (default 6) se calcula `neto = sueldo` por aseo. Round
 * 2026-05-11: las columnas `bono` y `descuento` per-aseo se eliminaron;
 * los flujos extra (bono extra, multa, adelanto, propina, reembolso) viven
 * en `gastos` y vienen separadamente como `MovimientoAseador[]`. El backend
 * (misFinanzasAseador) toma el id del JWT — no hay :id en path para evitar IDOR.
 *
 * `bono` y `descuento` se mantienen en el shape pero siempre devuelven 0;
 * son compat para clientes viejos del JSON que ya no consumimos en UI.
 */
export type AseoFinanzaItem = {
  id: number;
  fechaInicio: string | null;
  horaAseo: string | null;
  propiedadId: number | null;
  propiedadNombre: string;
  sueldo: number;
  /** @deprecated Round 2026-05-11. Siempre 0 — los bonos viven en `MovimientoAseador`. */
  bono: number;
  /** @deprecated Round 2026-05-11. Siempre 0 — los descuentos viven en `MovimientoAseador`. */
  descuento: number;
  neto: number;
  estado: "pendiente" | "en_proceso" | "completado" | "pagado";
  /** Aseo asignado con fecha futura (aún no ocurre). Se muestra como pendiente
   *  con tag "Programado"; no es cobrable hasta completarse. Round 2026-07-13. */
  esFuturo?: boolean;
  fechaPago: string | null;
  observaciones: string | null;
  /**
   * Round 2026-08-25 — aseo con equipo 1..N: `sueldo`/`neto` son LA PARTE de
   * este aseador; `sueldoTotalAseo` es el sueldo completo del aseo y
   * `totalAseadoras` cuántas se lo repartieron.
   */
  aseoCompartido?: boolean;
  totalAseadoras?: number;
  sueldoTotalAseo?: number;
};

/**
 * Conceptos del enum UX `concepto_aseador` (Fase 2). Etiqueta corta del
 * movimiento — distinta de `categoria`, que es la dimensión contable. Los
 * labels se calculan en el backend (`CONCEPTO_LABELS`); el frontend solo
 * los pinta.
 */
export type ConceptoAseador =
  | "adelanto"
  | "bono_extra"
  | "descuento_disciplinario"
  | "reembolso_gasto"
  | "propina"
  | "multa"
  | "otro";

export type MovimientoEstado = "pendiente" | "pagado" | "denegado";

/**
 * Movimiento extra en el ledger del aseador (Fase 2). Cubre adelantos,
 * bonos sueltos, multas, reembolsos. Vive en la tabla `gastos`; el
 * backend filtra por `pagado_a.tipo=3` o `pagado_por.tipo=3` y mapea
 * con signo positivo o negativo según la dirección.
 */
export type MovimientoAseador = {
  id: number;
  /** Una asignación de lavandería tiene identidad propia y no es un gasto de AF. */
  origen?: "lavanderia_asignacion";
  asignacionId?: number;
  lavanderiaId?: number;
  lavanderiaNombre?: string;
  modalidad?: "directo" | "af_intermediario";
  notas?: string | null;
  revision?: string;
  /** `pago_recibido` = aseador recibe (signo +1). `pago_emitido` = aseador paga (signo −1). */
  tipo: "pago_recibido" | "pago_emitido";
  /** Monto bruto siempre ≥ 0. Aplicar `signo` para el efecto neto. */
  monto: number;
  signo: 1 | -1;
  conceptoCodigo: ConceptoAseador | null;
  conceptoLabel: string | null;
  categoria: string | null;
  rolAf: "paga" | "recibe" | "intermediario" | null;
  fecha: string | null;
  estado: MovimientoEstado | "sin_definir";
  fechaPago: string | null;
  pagadoPorIdAdmin: number | null;
  descripcion: string;
  nombre: string | null;
  propiedadId: number | null;
  /**
   * Ocurrencia mensual de un flujo REGULAR (round 2026-07-27): una suscripción
   * o arriendo que AF le paga a esta persona todos los meses.
   *
   * El backend NO materializa una fila por mes — el regular es una sola fila de
   * `gastos` que se expande al construir el ledger. Consecuencias para la UI:
   *   • `id` es el de la fila regular y SE REPITE entre meses. La identidad de
   *     la ocurrencia es el par (`id`, `periodo`) → usarlo también en el rowKey.
   *   • Aprobar / denegar / revertir van al endpoint de regulares
   *     (`marcarPagoRegular*`), no al de movimientos.
   *   • No se edita ni se borra desde acá: eso se hace en /flujos, sobre el
   *     flujo completo (incluida su vigencia).
   */
  esRegular?: boolean;
  /** 'YYYY-MM' del mes que representa esta ocurrencia. Solo si `esRegular`. */
  periodo?: string | null;
  /** Día del mes en que se paga el regular (1-28). Solo si `esRegular`. */
  diaMes?: number | null;
  contraparte: {
    tipo: number | null;
    nombre: string | null;
    usuarioId: number | null;
    recipientId: number | null;
  } | null;
};

export type MesFinanzaAseador = {
  year: number;
  month: number; // 1..12
  // Solo en modo `weeks`: `weekStart` = lunes ISO YYYY-MM-DD del bucket.
  // Permite distinguir paneles que comparten year+month (ej. dos semanas
  // dentro del mismo mes). El frontend lo usa como sufijo del key del
  // Collapse para no colisionar.
  weekStart?: string;
  label: string;

  // ─ Aseos (Fase 1) ────────────────────────────────────────────
  totalSueldo: number;
  /** @deprecated Round 2026-05-11. Siempre 0. Los bonos viven en `movimientos`. */
  totalBono: number;
  /** @deprecated Round 2026-05-11. Siempre 0. Los descuentos viven en `movimientos`. */
  totalDescuento: number;
  totalNeto: number;
  totalPagado: number;
  totalPendiente: number;
  cantidadAseos: number;
  aseos: AseoFinanzaItem[];

  // ─ Movimientos extra (Fase 2) ────────────────────────────────
  totalMovimientosRecibidos: number;
  totalMovimientosEmitidos: number;
  totalMovimientosNeto: number;
  totalMovimientosPagado: number; // signed, solo estado='pagado'
  totalMovimientosPendiente: number; // signed, solo estado='pendiente'
  totalMovimientosSinEstado?: number; // asignaciones de lavandería cuyo estado aún no se indicó
  cantidadMovimientos: number;
  cantidadPendientes: number;
  movimientos: MovimientoAseador[];

  // ─ Total general (aseos + movimientos no-denegados) ─────────
  totalGeneralNeto: number;
  totalGeneralPagado: number;
  totalGeneralPendiente: number;
};

export type FinanzasAseadorResponse = {
  status: string;
  message: string;
  data: {
    rango: { from: string; to: string };
    meses: MesFinanzaAseador[];
  };
};

/**
 * Shape de la vista admin (`GET /api/finanzas/aseador/:id`). Idéntico al
 * del aseador + un bloque `aseador` con id/nombre — el panel admin lo usa
 * en el header del detalle.
 */
export type FinanzasAseadorAdminResponse = {
  status: string;
  message: string;
  data: {
    rango: { from: string; to: string };
    meses: MesFinanzaAseador[];
    // `id` es `null` en el caso especial "Sin aseador designado"
    // (getFinanzasAseadorSinAsignar); un aseador real siempre trae number.
    aseador: { id: number | null; nombre: string };
  };
};

export const finanzasApi = {
  /**
   * Resumen financiero del aseador logueado (últimos N meses con desglose
   * por aseo + totales por mes). El id del aseador sale del JWT en el
   * backend — no se pasa por path. `months` clamped a [1, 24].
   */
  getMisFinanzasAseador: (
    // Modo semanas (round 2026-07-06 — los pagos al aseador son semanales L-D,
    // los lunes): `weeks` pide N buckets semanales. `month` (round 2026-07-09,
    // "YYYY-MM") pide un mes puntual desplegado en sus semanas L-D. `months`
    // queda como compat/fallback. El backend resuelve en `parseRangeFromQuery`
    // (precedencia: month > weeks > months).
    opts: { weeks?: number; months?: number; month?: string } = { months: 6 },
  ): Promise<FinanzasAseadorResponse | null> => {
    const qs = opts.month
      ? `month=${encodeURIComponent(opts.month)}`
      : opts.weeks != null
        ? `weeks=${opts.weeks}`
        : `months=${opts.months ?? 6}`;
    return apiFetch<FinanzasAseadorResponse>(`/finanzas/aseador/me?${qs}`, {
      errorMsg: ERROR_MSG,
    });
  },

  /**
   * Vista admin: finanzas de un aseador específico (mismos datos que ve
   * el aseador en su propio panel + bloque `aseador` con nombre/id).
   *
   * Solo admin/superadmin. El backend valida que el `:id` corresponda a
   * un usuario con rol "aseador" — pasar un id de cliente o admin retorna
   * 400. Frontend: `/finanzas-aseador-admin` (selector de aseador).
   */
  /**
   * Vista admin: finanzas de un aseador específico. Soporta tres modos
   * de rango (precedencia: range > weeks > months > default 6 meses):
   *
   *  - `{ months }` → últimos N meses calendario (default 6).
   *  - `{ weeks }`  → últimas N semanas ISO 8601 (Lun-Dom). Cada semana
   *    como un panel separado — pensado para el flujo de pagos semanal.
   *  - `{ range: { from, to, label? } }` → rango libre (un solo panel).
   *    Útil para "ir a la semana del 1 al 7 de abril".
   */
  getFinanzasAseadorById: (
    aseadorId: number,
    opts?: {
      months?: number;
      weeks?: number;
      range?: { from: string; to: string; label?: string };
    },
  ): Promise<FinanzasAseadorAdminResponse | null> => {
    const qs = new URLSearchParams();
    if (opts?.range?.from && opts?.range?.to) {
      qs.set("from", opts.range.from);
      qs.set("to", opts.range.to);
      if (opts.range.label) qs.set("label", opts.range.label);
    } else if (opts?.weeks) {
      qs.set("weeks", String(opts.weeks));
    } else {
      qs.set("months", String(opts?.months ?? 6));
    }
    return apiFetch<FinanzasAseadorAdminResponse>(
      `/finanzas/aseador/${aseadorId}?${qs.toString()}`,
      { errorMsg: ERROR_MSG },
    );
  },

  /**
   * Caso "Sin aseador designado" (round 2026-07-12) — admin-only, SOLO
   * LECTURA. Devuelve los aseos con `id_usuario IS NULL` (abiertos / no
   * asignados) del rango, SIN movimientos y SIN acciones de pago (pagar un
   * aseo sin aseador sería "pago fantasma", ver flujo-financiero.md §6.6).
   * Mismo shape que `getFinanzasAseadorById` (`aseador.id = null`,
   * `nombre = "Sin aseador designado"`). Endpoint dedicado sin `:id`.
   */
  getFinanzasAseadorSinAsignar: (
    opts?: {
      months?: number;
      weeks?: number;
      range?: { from: string; to: string; label?: string };
    },
  ): Promise<FinanzasAseadorAdminResponse | null> => {
    const qs = new URLSearchParams();
    if (opts?.range?.from && opts?.range?.to) {
      qs.set("from", opts.range.from);
      qs.set("to", opts.range.to);
      if (opts.range.label) qs.set("label", opts.range.label);
    } else if (opts?.weeks) {
      qs.set("weeks", String(opts.weeks));
    } else {
      qs.set("months", String(opts?.months ?? 6));
    }
    return apiFetch<FinanzasAseadorAdminResponse>(
      `/finanzas/aseador/sin-asignar?${qs.toString()}`,
      { errorMsg: ERROR_MSG },
    );
  },

  /**
   * POST atajo (admin) — registra un movimiento extra en el ledger del
   * aseador. Crea una fila en `gastos` con defaults inteligentes y arma
   * `pagado_a`/`pagado_por` según la **contraparte** elegida:
   *
   *  - `{ kind: "af" }` (default): AF como caja. Usa `rolAf` para
   *    decidir paga/recibe; el movimiento sí entra a la utilidad AF.
   *  - `{ kind: "propiedad", propiedadId }`: el dinero pasa cliente↔aseador
   *    vía AF. Forza `rol_af='intermediario'` y setea `propiedad_id` para
   *    que aparezca en la liquidación al cliente (`/finanzas-clientes`).
   *  - `{ kind: "externo", nombreExterno? }`: tercero externo. AF sólo
   *    audita (no afecta utilidad).
   *
   * Ver `docs/modulo-finanzas-aseador.md` §3 y §8.
   */
  crearMovimientoAseador: (
    aseadorId: number,
    body: {
      monto: number;
      signo: 1 | -1;
      concepto: ConceptoAseador;
      descripcion: string;
      fecha: string; // YYYY-MM-DD
      rolAf?: "paga" | "recibe" | "intermediario";
      contraparte?:
        | { kind: "af" }
        | { kind: "propiedad"; propiedadId: number }
        | { kind: "externo"; nombreExterno?: string };
    },
  ): Promise<{ status: string; data: { id: number } } | null> =>
    apiFetch(`/finanzas/aseador/${aseadorId}/movimientos`, {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Movimiento registrado (pendiente de aprobación)",
      errorMsg: "No se pudo registrar el movimiento",
    }),

  /** PATCH admin — aprueba un movimiento pendiente (estado_pago='pagado'). */
  aprobarMovimientoAseador: (
    aseadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/${movimientoId}/aprobar`,
      {
        method: "PATCH",
        successMsg: "Movimiento aprobado",
        errorMsg: "No se pudo aprobar el movimiento",
      },
    ),

  /** PATCH admin — deniega un movimiento (queda como auditoría, no suma). */
  denegarMovimientoAseador: (
    aseadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/${movimientoId}/denegar`,
      {
        method: "PATCH",
        successMsg: "Movimiento denegado",
        errorMsg: "No se pudo denegar el movimiento",
      },
    ),

  /** PATCH admin — revierte aprobación/denegación de movimiento → pendiente. */
  revertirMovimientoAseador: (
    aseadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/${movimientoId}/revertir`,
      {
        method: "PATCH",
        successMsg: "Movimiento revertido a pendiente",
        errorMsg: "No se pudo revertir el movimiento",
      },
    ),

  /**
   * PATCH admin — fija el estado del pago de UN MES de un flujo regular
   * dirigido al aseador (round 2026-07-27).
   *
   * No usa el endpoint de movimientos porque no hay una fila por mes: el
   * regular es una sola fila y la ocurrencia se identifica con
   * (`gastoId`, `periodo`). Por eso tampoco hay crear ni borrar — la
   * existencia de la ocurrencia la define la vigencia del flujo en /flujos.
   */
  marcarPagoRegularAseador: (
    aseadorId: number,
    gastoId: number,
    periodo: string,
    estado: MovimientoEstado,
  ): Promise<{ status: string } | null> =>
    apiFetch(`/finanzas/aseador/${aseadorId}/regulares/${gastoId}/${periodo}`, {
      method: "PATCH",
      body: JSON.stringify({ estado }),
      successMsg:
        estado === "pagado"
          ? "Pago del mes aprobado"
          : estado === "denegado"
            ? "Pago del mes denegado"
            : "Pago del mes vuelto a pendiente",
      errorMsg: "No se pudo actualizar el pago del mes",
    }),

  /** PATCH admin — edita campos de un movimiento. Si estaba pagado/denegado,
   *  vuelve a pendiente automáticamente. */
  editarMovimientoAseador: (
    aseadorId: number,
    movimientoId: number,
    body: {
      monto?: number;
      concepto?: string;
      descripcion?: string;
      fecha?: string;
      rolAf?: "paga" | "recibe" | "intermediario";
      // C4 (round 2026-06-20, D-C): corregir la dirección del movimiento.
      // +1 = el aseador recibe; −1 = el aseador paga. El backend re-deriva
      // pagado_por/pagado_a (y el rol AF cuando la contraparte es AF).
      signo?: 1 | -1;
    },
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/${movimientoId}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
        successMsg: "Movimiento editado",
        errorMsg: "No se pudo editar el movimiento",
      },
    ),

  /**
   * DELETE admin — borra un movimiento del aseador. Solo `pendiente` o
   * `denegado` (C5, D-D); un `pagado` se corrige con revertir. El backend
   * hace cumplir la regla y responde 409 si el estado no lo permite.
   */
  borrarMovimientoAseador: (
    aseadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/${movimientoId}`,
      {
        method: "DELETE",
        successMsg: "Movimiento borrado",
        errorMsg: "No se pudo borrar el movimiento",
      },
    ),

  /** PATCH admin — paga un aseo individual desde el panel del aseador. */
  pagarAseoIndividual: (
    aseadorId: number,
    cleaningId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/aseos/${cleaningId}/pagar`,
      {
        method: "PATCH",
        successMsg: "Aseo marcado como pagado",
        errorMsg: "No se pudo pagar el aseo",
      },
    ),

  /** PATCH admin — desmarca un aseo pagado (vuelve a 'completado'). */
  desmarcarPagoAseo: (
    aseadorId: number,
    cleaningId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/aseos/${cleaningId}/desmarcar-pagado`,
      {
        method: "PATCH",
        successMsg: "Pago de aseo revertido",
        errorMsg: "No se pudo revertir el pago del aseo",
      },
    ),

  /**
   * POST admin — aprueba en bulk todos los pendientes del aseador en un
   * rango. Pensado para el flujo del lunes (`docs/modulo-finanzas-aseador.md`
   * §9). Default `rango='semana_anterior'` (mismo rango que el pago de
   * aseos). Pasar `rango='rango_visible'` con `from`/`to` para aprobar
   * solo lo que se está viendo en pantalla.
   */
  aprobarPendientesBulkAseador: (
    aseadorId: number,
    body: {
      rango: "semana_anterior" | "rango_visible";
      from?: string; // YYYY-MM-DD, requerido si rango='rango_visible'
      to?: string;
    },
  ): Promise<{
    status: string;
    data: { count: number; rango: { from: string; to: string } };
  } | null> =>
    apiFetch(
      `/finanzas/aseador/${aseadorId}/movimientos/aprobar-pendientes-bulk`,
      {
        method: "POST",
        body: JSON.stringify(body),
        successMsg: "Movimientos generales aprobados. Los pagos de lavandería se gestionan individualmente.",
        errorMsg: "No se pudo aprobar el lote",
      },
    ),
};
